import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  CircularProgress,
  Button,
  Snackbar,
  Alert,
  TextField,
  Card,
  CardContent,
  IconButton,
  Stack,
  Paper,
  Chip,
} from "@mui/material";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import TPC_URL from "../../../utils/TPC_URL";
import axios from "axios";
import DeleteIcon from "@mui/icons-material/Delete";
import FactoryIcon from "@mui/icons-material/Factory";
import PublishIcon from "@mui/icons-material/Publish";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";
import "dayjs/locale/ko";

dayjs.locale("ko");

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: calc(100vh - 65px);
  background-color: #f5f5f5;
  overflow: hidden;
  box-sizing: border-box;
`;

const FrameContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  flex: 1;
  overflow: hidden;
`;

const ToolbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background-color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 10;
`;

const ContentContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const DateSection = styled.div`
  margin-bottom: 24px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  background-color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const DateHeader = styled.div`
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ShiftContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  padding: 20px;
`;

const ShiftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ShiftLabel = styled.div`
  background-color: ${(props) => (props.shift === 1 ? "#e3f2fd" : "#fce4ec")};
  color: ${(props) => (props.shift === 1 ? "#1565c0" : "#c2185b")};
  padding: 8px 12px;
  border-radius: 6px;
  text-align: center;
  font-weight: 600;
  font-size: 0.95rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const EmptySlot = styled.div`
  min-height: 100px;
  border: 2px dashed #ccc;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
`;

function WorkOrderRegister() {
  const { group_code, project_code } = useParams();

  const [loading, setLoading] = useState(false);
  const [groupedByDate, setGroupedByDate] = useState({});
  const [customQuantities, setCustomQuantities] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [modifiedGroups, setModifiedGroups] = useState(new Set());

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    loadTempData();
  }, [group_code, project_code]);

  const loadTempData = () => {
    try {
      setLoading(true);
      const storageKey = `temp_instruction_${group_code}_${project_code}`;
      const savedData = JSON.parse(localStorage.getItem(storageKey) || "[]");

      if (savedData.length === 0) {
        setGroupedByDate({});
        setLoading(false);
        return;
      }

      const byDate = {};
      const originalQty = {};
      const initialCustomQty = {};

      savedData.forEach((group) => {
        if (!byDate[group.date]) {
          byDate[group.date] = {
            day: [],
            night: [],
          };
        }

        if (group.shift_id === 1) {
          byDate[group.date].day.push(group);
        } else if (group.shift_id === 2) {
          byDate[group.date].night.push(group);
        }

        originalQty[group.id] = {};
        group.items.forEach((item) => {
          const key = `${group.id}_${item.grid_id}`;
          originalQty[group.id][item.grid_id] = item.custom_qty || 0;
          initialCustomQty[key] = item.custom_qty || "";
        });
      });

      const sortedByDate = Object.keys(byDate)
        .sort((a, b) => dayjs(a).diff(dayjs(b)))
        .reduce((acc, date) => {
          acc[date] = byDate[date];
          return acc;
        }, {});

      setGroupedByDate(sortedByDate);
      setOriginalData(originalQty);
      setCustomQuantities(initialCustomQty);
      setModifiedGroups(new Set());
    } catch (error) {
      console.error("Error loading temp data:", error);
      setSnackbar({
        open: true,
        message: "임시저장 데이터를 불러오는 중 오류가 발생했습니다.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCustomQtyChange = (groupId, gridId, value) => {
    const key = `${groupId}_${gridId}`;
    setCustomQuantities((prev) => ({
      ...prev,
      [key]: value,
    }));

    setTimeout(() => {
      checkIfModified(groupId);
    }, 0);
  };

  const checkIfModified = (groupId) => {
    let currentGroup = null;
    Object.entries(groupedByDate).forEach(([date, shifts]) => {
      const allGroups = [...shifts.day, ...shifts.night];
      const found = allGroups.find((g) => g.id === groupId);
      if (found) currentGroup = found;
    });

    if (!currentGroup) return;

    const allSame = currentGroup.items.every((item) => {
      const key = `${groupId}_${item.grid_id}`;
      const currentValue = customQuantities[key];

      const currentNum =
        currentValue === "" || currentValue === undefined
          ? 0
          : parseInt(currentValue) || 0;

      const origValue = originalData[groupId]?.[item.grid_id] || 0;

      return currentNum === origValue;
    });

    setModifiedGroups((prev) => {
      const newSet = new Set(prev);
      if (allSame) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const handlePublish = async (group) => {
    if (
      !window.confirm(
        `${group.machine_name} (${group.shift_name})을(를) 발행하시겠습니까?`
      )
    ) {
      return;
    }
  
    try {
      const instructionData = group.items.map((item) => {
        const customKey = `${group.id}_${item.grid_id}`;
        const customValue = customQuantities[customKey];
  
        // ✅ 사용자가 입력했는지 체크
        const hasCustomInput = 
          customValue !== "" && 
          customValue !== undefined && 
          customValue !== null;
  
        // ✅ 입력 안 하면 null, 입력하면 숫자
        const customQty = hasCustomInput ? parseInt(customValue, 10) : null;
        const productionQty = hasCustomInput ? parseInt(customValue, 10) : item.qty;
  
        return {
          item_id: item.item_id,
          item_code: item.item_code,
          shift_id: group.shift_id,
          machine_id: group.machine_id,
          ai_qty: item.qty,
          custom_qty: customQty,  // null 또는 숫자
          production_qty: productionQty,
          instruction_date: group.date,
        };
      });
  
      await axios.post(`${TPC_URL}/api/instruction/postInstruction`, {
        group_code,
        project_code,
        info: instructionData,
      });
  
      setSnackbar({
        open: true,
        message: "작업지시가 발행되었습니다.",
        severity: "success",
      });
  
      removeFromLocalStorage(group.id);
      removeGroupFromState(group.id, group.date, group.shift_id);
    } catch (error) {
      console.error("Error publishing work order:", error);
      setSnackbar({
        open: true,
        message: "작업지시 발행 중 오류가 발생했습니다.",
        severity: "error",
      });
    }
  };
  
  const removeFromLocalStorage = (groupId) => {
    const storageKey = `temp_instruction_${group_code}_${project_code}`;
    const existing = JSON.parse(localStorage.getItem(storageKey) || "[]");
    const filtered = existing.filter((g) => g.id !== groupId);
    localStorage.setItem(storageKey, JSON.stringify(filtered));
  };

  const removeGroupFromState = (groupId, date, shiftId) => {
    setGroupedByDate((prev) => {
      const updated = { ...prev };
      const shiftKey = shiftId === 1 ? "day" : "night";

      updated[date][shiftKey] = updated[date][shiftKey].filter(
        (g) => g.id !== groupId
      );

      if (
        updated[date].day.length === 0 &&
        updated[date].night.length === 0
      ) {
        delete updated[date];
      }

      return updated;
    });

    setModifiedGroups((prev) => {
      const newSet = new Set(prev);
      newSet.delete(groupId);
      return newSet;
    });
  };

  const handleDeleteGroup = (group) => {
    if (
      !window.confirm(
        `${group.machine_name} (${group.shift_name})을(를) 삭제하시겠습니까?`
      )
    ) {
      return;
    }

    removeFromLocalStorage(group.id);
    removeGroupFromState(group.id, group.date, group.shift_id);

    setSnackbar({
      open: true,
      message: "임시저장 데이터가 삭제되었습니다.",
      severity: "info",
    });
  };

  const getTotalAiQty = (group) => {
    if (!group || !Array.isArray(group.items)) return 0;
    return group.items.reduce((sum, item) => sum + (item.qty || 0), 0);
  };
  
  const getTotalCustomQty = (group) => {
    if (!group || !Array.isArray(group.items)) return 0;
    return group.items.reduce((sum, item) => {
      const customQty = customQuantities[`${group.id}_${item.grid_id}`];
      const qtyNum =
        customQty === "" || customQty === undefined
          ? 0
          : parseInt(customQty) || 0;
      return sum + qtyNum;
    }, 0);
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const isGroupModified = (groupId) => {
    return modifiedGroups.has(groupId);
  };

  if (loading) {
    return (
      <Wrapper>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <CircularProgress size={60} />
        </Box>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <FrameContainer>
        <ToolbarContainer>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ fontSize: "22px !important", color: "#111" }}
          >
            임시저장 작업지시서 발행
          </Typography>
          <Button
            variant="outlined"
            onClick={loadTempData}
            startIcon={<RefreshIcon />}
          >
            새로고침
          </Button>
        </ToolbarContainer>

        <ContentContainer>
          {Object.keys(groupedByDate).length === 0 ? (
            <Paper
              sx={{
                p: 4,
                textAlign: "center",
                bgcolor: "white",
              }}
            >
              <Typography variant="h6" color="text.secondary">
                임시저장된 데이터가 없습니다.
              </Typography>
            </Paper>
          ) : (
            Object.entries(groupedByDate).map(([date, shifts]) => (
              <DateSection key={date}>
                <DateHeader>
                  <Typography variant="h6" fontWeight={600} color="#2c3e50">
                    📅 {dayjs(date).format("YYYY년 MM월 DD일")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {dayjs(date).format("dddd")}
                  </Typography>
                </DateHeader>

                <ShiftContainer>
                  {/* 주간 (왼쪽) */}
                  <ShiftColumn>
                    <ShiftLabel shift={1}>🌞 주간</ShiftLabel>

                    {shifts.day.length > 0 ? (
                      shifts.day.map((group) => {
                        const modified = isGroupModified(group.id);

                        return (
                          <Card
                            key={group.id}
                            sx={{
                              boxShadow: 2,
                              borderRadius: 2,
                              border: modified
                                ? "2px solid #ff9800"
                                : "1px solid #e0e0e0",
                              transition: "all 0.3s",
                              "&:hover": {
                                boxShadow: 4,
                              },
                            }}
                          >
                            <CardContent sx={{ p: 2.5 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  mb: 1.5,
                                  pb: 1.5,
                                  borderBottom: "2px solid #e0e0e0",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <FactoryIcon sx={{ color: "#666" }} />
                                  <Typography variant="h6" fontWeight={600}>
                                    {group.machine_name}
                                  </Typography>
                                  {modified && (
                                    <Chip
                                      label="수정됨"
                                      color="warning"
                                      size="small"
                                      icon={<EditIcon fontSize="small" />}
                                    />
                                  )}
                                </Box>

                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Chip
                                    label={`저장: ${dayjs(
                                      group.saved_at
                                    ).format("MM/DD HH:mm")}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                  <Button
                                    variant="contained"
                                    startIcon={<PublishIcon />}
                                    onClick={() => handlePublish(group)}
                                    size="small"
                                    color="primary"
                                  >
                                    발행
                                  </Button>
                                  <IconButton
                                    color="error"
                                    onClick={() => handleDeleteGroup(group)}
                                    size="small"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                              </Box>

                              <Box
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: "repeat(3, 1fr)",
                                  gap: 1,
                                  mb: 1.5,
                                  p: 1,
                                  bgcolor: "#f9f9f9",
                                  borderRadius: 1,
                                }}
                              >
                                <Box sx={{ textAlign: "center" }}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    AI 수량
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="primary"
                                    fontWeight="bold"
                                  >
                                    {getTotalAiQty(group).toLocaleString()}
                                  </Typography>
                                </Box>
                                <Box sx={{ textAlign: "center" }}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    설정 수량
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="secondary"
                                    fontWeight="bold"
                                  >
                                    {getTotalCustomQty(group).toLocaleString()}
                                  </Typography>
                                </Box>
                                <Box sx={{ textAlign: "center" }}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    품목 수
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    fontWeight="bold"
                                  >
                                    {group.items.length}
                                  </Typography>
                                </Box>
                              </Box>

                              <Stack spacing={1.5}>
                                {group.items.map((item, index) => {
                                  const currentValue =
                                    customQuantities[
                                      `${group.id}_${item.grid_id}`
                                    ];
                                  const originalValue =
                                    originalData[group.id]?.[item.grid_id] || 0;

                                  const currentNum =
                                    currentValue === "" ||
                                    currentValue === undefined
                                      ? 0
                                      : parseInt(currentValue) || 0;

                                  const isModified = currentNum !== originalValue;

                                  return (
                                    <Paper
                                      key={item.grid_id}
                                      elevation={0}
                                      sx={{
                                        p: 1.5,
                                        bgcolor:
                                          index % 2 === 0 ? "#fafafa" : "white",
                                        border: isModified
                                          ? "1px solid #ff9800"
                                          : "1px solid #e0e0e0",
                                        borderRadius: 1,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "space-between",
                                          gap: 2,
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                          }}
                                        >
                                          <Typography
                                            variant="subtitle2"
                                            fontWeight="bold"
                                          >
                                            {item.item_code}
                                          </Typography>
                                          {isModified && (
                                            <Chip
                                              label="수정"
                                              size="small"
                                              color="warning"
                                              sx={{
                                                height: 20,
                                                fontSize: 10,
                                              }}
                                            />
                                          )}
                                        </Box>

                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              width: 80,
                                              textAlign: "center",
                                              p: 0.5,
                                              bgcolor: "white",
                                              borderRadius: 1,
                                              border: "1px solid #e3f2fd",
                                            }}
                                          >
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              display="block"
                                            >
                                              AI
                                            </Typography>
                                            <Typography
                                              variant="body2"
                                              color="primary"
                                              fontWeight="bold"
                                            >
 {(item.qty || 0).toLocaleString()}                                            </Typography>
                                          </Box>

                                          <TextField
                                            placeholder="설정"
                                            type="number"
                                            size="small"
                                            value={currentValue || ""}
                                            onChange={(e) =>
                                              handleCustomQtyChange(
                                                group.id,
                                                item.grid_id,
                                                e.target.value
                                              )
                                            }
                                            sx={{ width: 100 }}
                                            InputProps={{
                                              inputProps: { min: 0 },
                                            }}
                                          />
                                        </Box>
                                      </Box>
                                    </Paper>
                                  );
                                })}
                              </Stack>
                            </CardContent>
                          </Card>
                        );
                      })
                    ) : (
                      <EmptySlot>
                        <Typography variant="body2">
                          주간 데이터 없음
                        </Typography>
                      </EmptySlot>
                    )}
                  </ShiftColumn>

                  {/* 야간 (오른쪽) */}
                  <ShiftColumn>
                    <ShiftLabel shift={2}>🌙 야간</ShiftLabel>

                    {shifts.night.length > 0 ? (
                      shifts.night.map((group) => {
                        const modified = isGroupModified(group.id);

                        return (
                          <Card
                            key={group.id}
                            sx={{
                              boxShadow: 2,
                              borderRadius: 2,
                              border: modified
                                ? "2px solid #ff9800"
                                : "1px solid #e0e0e0",
                              transition: "all 0.3s",
                              "&:hover": {
                                boxShadow: 4,
                              },
                            }}
                          >
                            <CardContent sx={{ p: 2.5 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  mb: 1.5,
                                  pb: 1.5,
                                  borderBottom: "2px solid #e0e0e0",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <FactoryIcon sx={{ color: "#666" }} />
                                  <Typography variant="h6" fontWeight={600}>
                                    {group.machine_name}
                                  </Typography>
                                  {modified && (
                                    <Chip
                                      label="수정됨"
                                      color="warning"
                                      size="small"
                                      icon={<EditIcon fontSize="small" />}
                                    />
                                  )}
                                </Box>

                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Chip
                                    label={`저장: ${dayjs(
                                      group.saved_at
                                    ).format("MM/DD HH:mm")}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                  <Button
                                    variant="contained"
                                    startIcon={<PublishIcon />}
                                    onClick={() => handlePublish(group)}
                                    size="small"
                                    color="primary"
                                  >
                                    발행
                                  </Button>
                                  <IconButton
                                    color="error"
                                    onClick={() => handleDeleteGroup(group)}
                                    size="small"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                              </Box>

                              <Box
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: "repeat(3, 1fr)",
                                  gap: 1,
                                  mb: 1.5,
                                  p: 1,
                                  bgcolor: "#f9f9f9",
                                  borderRadius: 1,
                                }}
                              >
                                <Box sx={{ textAlign: "center" }}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    AI 수량
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="primary"
                                    fontWeight="bold"
                                  >
                                    {getTotalAiQty(group).toLocaleString()}
                                  </Typography>
                                </Box>
                                <Box sx={{ textAlign: "center" }}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    설정 수량
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="secondary"
                                    fontWeight="bold"
                                  >
                                    {getTotalCustomQty(group).toLocaleString()}
                                  </Typography>
                                </Box>
                                <Box sx={{ textAlign: "center" }}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    품목 수
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    fontWeight="bold"
                                  >
                                    {group.items.length}
                                  </Typography>
                                </Box>
                              </Box>

                              <Stack spacing={1.5}>
                                {group.items.map((item, index) => {
                                  const currentValue =
                                    customQuantities[
                                      `${group.id}_${item.grid_id}`
                                    ];
                                  const originalValue =
                                    originalData[group.id]?.[item.grid_id] || 0;

                                  const currentNum =
                                    currentValue === "" ||
                                    currentValue === undefined
                                      ? 0
                                      : parseInt(currentValue) || 0;

                                  const isModified = currentNum !== originalValue;

                                  return (
                                    <Paper
                                      key={item.grid_id}
                                      elevation={0}
                                      sx={{
                                        p: 1.5,
                                        bgcolor:
                                          index % 2 === 0 ? "#fafafa" : "white",
                                        border: isModified
                                          ? "1px solid #ff9800"
                                          : "1px solid #e0e0e0",
                                        borderRadius: 1,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "space-between",
                                          gap: 2,
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                          }}
                                        >
                                          <Typography
                                            variant="subtitle2"
                                            fontWeight="bold"
                                          >
                                            {item.item_code}
                                          </Typography>
                                          {isModified && (
                                            <Chip
                                              label="수정"
                                              size="small"
                                              color="warning"
                                              sx={{
                                                height: 20,
                                                fontSize: 10,
                                              }}
                                            />
                                          )}
                                        </Box>

                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              width: 80,
                                              textAlign: "center",
                                              p: 0.5,
                                              bgcolor: "white",
                                              borderRadius: 1,
                                              border: "1px solid #e3f2fd",
                                            }}
                                          >
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              display="block"
                                            >
                                              AI
                                            </Typography>
                                            <Typography
                                              variant="body2"
                                              color="primary"
                                              fontWeight="bold"
                                            >
                                              {item.qty.toLocaleString()}
                                            </Typography>
                                          </Box>

                                          <TextField
                                            placeholder="설정"
                                            type="number"
                                            size="small"
                                            value={currentValue || ""}
                                            onChange={(e) =>
                                              handleCustomQtyChange(
                                                group.id,
                                                item.grid_id,
                                                e.target.value
                                              )
                                            }
                                            sx={{ width: 100 }}
                                            InputProps={{
                                              inputProps: { min: 0 },
                                            }}
                                          />
                                        </Box>
                                      </Box>
                                    </Paper>
                                  );
                                })}
                              </Stack>
                            </CardContent>
                          </Card>
                        );
                      })
                    ) : (
                      <EmptySlot>
                        <Typography variant="body2">
                          야간 데이터 없음
                        </Typography>
                      </EmptySlot>
                    )}
                  </ShiftColumn>
                </ShiftContainer>
              </DateSection>
            ))
          )}
        </ContentContainer>
      </FrameContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Wrapper>
  );
}

export default WorkOrderRegister;
